/**
 * MonthlyLabResults.js
 * ─────────────────────────────────────────────────────────────────────────────
 * DHIMS-style monthly lab activity report. Layout intentionally mirrors
 * MonthlyOpdMorbidityReturn.js so the two reports feel identical to the
 * user — same age-bucket header, same gender split, same per-section
 * filter, same global search.
 *
 * Data shape from /monthlyLabResults:
 *   { category, parameter, result, gender, age_group, count }
 *
 * In lab terms:
 *   category  → lab category from haematology_result.cat
 *                 (e.g. "urine r/e", "urine hcg (upt)", "fbc",
 *                  "malaria rdt")
 *   parameter → individual parameter from haematology_result.test
 *                 (e.g. "blood", "ph", "rbcs", "mps")
 *   result    → the recorded result value (flag → comments fallback)
 *                 (e.g. "Positive", "Negative", "+++", "Trace",
 *                  "normal", "6.0")
 *   gender    → "Male" / "Female"
 *   age_group → one of the AGE_GROUPS labels below
 *   count     → number of patients with that (parameter, result)
 *                  combination in that age/gender bucket in the month
 *
 * The frontend joins parameter+result into a single row label like
 *   "MPS — Positive" / "MPS — Negative"
 * so the report directly answers questions like "how many positive
 * malaria cases did we test in February" — exactly the result-distribution
 * the lab team needs for their reports, not just totals.
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, CircularProgress, Alert,
  TextField, Collapse, IconButton, Chip, InputAdornment
} from '@mui/material';
import {
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { monthlyLabResults } from '../BaseURL';

const AGE_GROUPS = [
  { key: '0-28 Day',   label: '<28days'  },
  { key: '01-11 Mon.', label: '1-11mths' },
  { key: '01-4 Yrs',  label: '1-4'      },
  { key: '05-09 Yrs', label: '5-9'      },
  { key: '10-14 Yrs', label: '10-14'    },
  { key: '15-17 Yrs', label: '15-17'    },
  { key: '18-19 Yrs', label: '18-19'    },
  { key: '20-34 Yrs', label: '20-34'    },
  { key: '35-49 Yrs', label: '35-49'    },
  { key: '50-59 Yrs', label: '50-59'    },
  { key: '60-69 Yrs', label: '60-69'    },
  { key: '70 + Yrs',  label: '70+'      },
];

const hCell = (extra = {}) => ({
  bgcolor: '#37474f', color: '#fff', fontWeight: 700,
  fontSize: '0.7rem', textAlign: 'center',
  border: '1px solid rgba(255,255,255,0.15)',
  whiteSpace: 'nowrap', py: 0.6, px: 0.5,
  ...extra,
});

const dCell = (extra = {}) => ({
  textAlign: 'center', fontSize: '0.72rem',
  border: '1px solid #e0e0e0', py: 0.4, px: 0.5,
  ...extra,
});

function SectionHeader({ label, open, onToggle, count, matchCount, globalFilter }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: '#455a64', color: '#fff', px: 1.5, py: 0.8,
        cursor: 'pointer', userSelect: 'none',
        '&:hover': { bgcolor: '#546e7a' },
      }}
    >
      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }}>
        {open ? <UpIcon fontSize="small" /> : <DownIcon fontSize="small" />}
      </IconButton>
      <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      {globalFilter && matchCount !== count && (
        <Chip
          label={`${matchCount} match`}
          size="small"
          sx={{ bgcolor: '#f59e0b', color: '#000', height: 18, fontSize: '0.65rem', mr: 0.5 }}
        />
      )}
      <Chip
        label={count}
        size="small"
        sx={{ bgcolor: '#78909c', color: '#fff', height: 18, fontSize: '0.65rem' }}
      />
    </Box>
  );
}

function LabSection({ category, parameters, dataMap, globalFilter }) {
  const [open, setOpen]     = useState(true);
  const [filter, setFilter] = useState('');

  // Apply both global and section-level filters. The section filter is the
  // mechanism the user asked for to handle huge-panel labs like Urine R/E:
  // there's no need to pre-pick which parameters appear, the nurse can just
  // type "blood" or "ph" or whatever they want to see and the rest hide.
  const filtered = useMemo(() => {
    let result = parameters;
    if (globalFilter.trim()) {
      result = result.filter(d =>
        d.toLowerCase().includes(globalFilter.toLowerCase())
      );
    }
    if (filter.trim()) {
      result = result.filter(d =>
        d.toLowerCase().includes(filter.toLowerCase())
      );
    }
    return result;
  }, [parameters, filter, globalFilter]);

  const hasGlobalMatch = useMemo(() => {
    if (!globalFilter.trim()) return false;
    return parameters.some(d =>
      d.toLowerCase().includes(globalFilter.toLowerCase())
    );
  }, [parameters, globalFilter]);

  const isOpen = open || hasGlobalMatch;

  const getVal = (param, gender, ageKey) =>
    dataMap[param]?.[gender]?.[ageKey] ?? 0;

  const rowTotal = (param, gender) =>
    AGE_GROUPS.reduce((s, ag) => s + getVal(param, gender, ag.key), 0);

  const grandTotal = (param) =>
    rowTotal(param, 'Male') + rowTotal(param, 'Female');

  const sectionColTotal = (gender, ageKey) =>
    filtered.reduce((s, d) => s + getVal(d, gender, ageKey), 0);

  const sectionGenderTotal = (gender) =>
    filtered.reduce((s, d) => s + rowTotal(d, gender), 0);

  const sectionGrandTotal = () =>
    filtered.reduce((s, d) => s + grandTotal(d), 0);

  if (globalFilter.trim() && filtered.length === 0) return null;

  return (
    <Box sx={{ mb: 0.5 }}>
      <SectionHeader
        label={category}
        open={isOpen}
        onToggle={() => setOpen(p => !p)}
        count={parameters.length}
        matchCount={filtered.length}
        globalFilter={globalFilter}
      />

      <Collapse in={isOpen}>
        {/* Section-level parameter filter — the key UX for big panels.
            Urine R/E has ~15 parameters; the nurse types "blood" and only
            that row stays. */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 0.6, bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <FilterIcon sx={{ fontSize: 14, color: '#90a4ae' }} />
          <TextField
            size="small"
            variant="standard"
            placeholder="Type here to filter rows in this category (e.g. 'positive', 'mps', 'trace')"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ flex: 1 }}
            inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
          />
          {filter && (
            <IconButton size="small" onClick={() => setFilter('')} sx={{ p: 0.2 }}>
              <ClearIcon sx={{ fontSize: 14, color: '#90a4ae' }} />
            </IconButton>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1600 }}>
            <colgroup>
              <col style={{ width: 180 }} />
              {AGE_GROUPS.map((_, i) => <col key={`mc${i}`} style={{ width: 50 }} />)}
              <col style={{ width: 50 }} />
              {AGE_GROUPS.map((_, i) => <col key={`fc${i}`} style={{ width: 50 }} />)}
              <col style={{ width: 50 }} />
              <col style={{ width: 55 }} />
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={hCell({ textAlign: 'left', pl: 1 })}>
                  Parameter / Result
                </TableCell>
                <TableCell colSpan={AGE_GROUPS.length + 1} sx={hCell({ bgcolor: '#1565c0' })}>
                  Male
                </TableCell>
                <TableCell colSpan={AGE_GROUPS.length + 1} sx={hCell({ bgcolor: '#880e4f' })}>
                  Female
                </TableCell>
                <TableCell rowSpan={2} sx={hCell({ bgcolor: '#2e7d32' })}>
                  Total
                </TableCell>
              </TableRow>

              <TableRow>
                {AGE_GROUPS.map(ag => (
                  <TableCell key={`mh-${ag.key}`} sx={hCell({ bgcolor: '#1976d2' })}>
                    {ag.label}
                  </TableCell>
                ))}
                <TableCell sx={hCell({ bgcolor: '#1565c0' })}>Sub</TableCell>
                {AGE_GROUPS.map(ag => (
                  <TableCell key={`fh-${ag.key}`} sx={hCell({ bgcolor: '#ad1457' })}>
                    {ag.label}
                  </TableCell>
                ))}
                <TableCell sx={hCell({ bgcolor: '#880e4f' })}>Sub</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={AGE_GROUPS.length * 2 + 4}
                    sx={{ textAlign: 'center', py: 2, color: '#90a4ae', fontSize: '0.75rem' }}
                  >
                    No matching parameters found
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((param, idx) => {
                const mTotal = rowTotal(param, 'Male');
                const fTotal = rowTotal(param, 'Female');
                const grand  = mTotal + fTotal;

                const isHighlighted = globalFilter.trim() &&
                  param.toLowerCase().includes(globalFilter.toLowerCase());

                return (
                  <TableRow
                    key={param}
                    sx={{
                      bgcolor: isHighlighted
                        ? '#fff8e1'
                        : idx % 2 === 0 ? '#fff' : '#fafafa',
                      '&:hover': { bgcolor: '#e3f2fd' },
                    }}
                  >
                    <TableCell sx={{
                      fontSize: '0.72rem', fontWeight: isHighlighted ? 700 : 500,
                      border: '1px solid #e0e0e0', pl: 1, py: 0.4,
                      whiteSpace: 'normal', lineHeight: 1.3,
                      color: isHighlighted ? '#e65100' : 'inherit',
                    }}>
                      {param}
                    </TableCell>

                    {AGE_GROUPS.map(ag => (
                      <TableCell key={`m-${ag.key}`} sx={dCell()}>
                        {getVal(param, 'Male', ag.key)}
                      </TableCell>
                    ))}
                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e3f2fd' })}>
                      {mTotal}
                    </TableCell>

                    {AGE_GROUPS.map(ag => (
                      <TableCell key={`f-${ag.key}`} sx={dCell()}>
                        {getVal(param, 'Female', ag.key)}
                      </TableCell>
                    ))}
                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#fce4ec' })}>
                      {fTotal}
                    </TableCell>

                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e8f5e9' })}>
                      {grand}
                    </TableCell>
                  </TableRow>
                );
              })}

              {filtered.length > 0 && (
                <TableRow sx={{ bgcolor: '#eceff1' }}>
                  <TableCell sx={{
                    fontSize: '0.7rem', fontWeight: 700,
                    border: '1px solid #cfd8dc', pl: 1, py: 0.5
                  }}>
                    Section Total
                  </TableCell>
                  {AGE_GROUPS.map(ag => (
                    <TableCell key={`st-m-${ag.key}`} sx={dCell({ fontWeight: 700 })}>
                      {sectionColTotal('Male', ag.key)}
                    </TableCell>
                  ))}
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e3f2fd' })}>
                    {sectionGenderTotal('Male')}
                  </TableCell>
                  {AGE_GROUPS.map(ag => (
                    <TableCell key={`st-f-${ag.key}`} sx={dCell({ fontWeight: 700 })}>
                      {sectionColTotal('Female', ag.key)}
                    </TableCell>
                  ))}
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#fce4ec' })}>
                    {sectionGenderTotal('Female')}
                  </TableCell>
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e8f5e9' })}>
                    {sectionGrandTotal()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

export default function MonthlyLabResults({ period }) {
  const [sections, setSections]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    if (!period) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setGlobalFilter('');

        const res    = await fetch(`${monthlyLabResults}?period=${period}`);
        const result = await res.json();

        if (!result.success) throw new Error(result.message || 'Failed to load');

        setSections(processData(result.data));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [period]);

  const toTitle = (str) =>
    String(str || '')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());

  // Result strings come in with mixed casing ("Positive" vs "positive",
  // "Negative" vs "Neg"). We display them as-typed but key on a normalised
  // form so "Positive" and "positive" collapse into one row. Result is not
  // title-cased the way parameter names are — we want "+++", "6.0",
  // ">30" to render exactly as the lab tech entered them.
  const normalizeResult = (r) => String(r || 'Unspecified').trim();

  const processData = (rows) => {
    const map = {};

    rows.forEach(({ category, parameter, result, gender, age_group, count }) => {
      const normCat     = toTitle(category);
      const normParam   = toTitle(parameter);
      // Join parameter + result into a single row label so the table
      // shows the result distribution directly. Example labels:
      //   "MPS — Positive"        (Malaria RDT)
      //   "MPS — Negative"
      //   "Blood — +++"           (Urine R/E)
      //   "Blood — Trace"
      //   "Blood — Negative"
      //   "PH — 6.0"              (numeric result; flag carries the value)
      const rowLabel    = `${normParam} — ${normalizeResult(result)}`;
      const normAge     = String(age_group || '').trim();
      const rawGender   = String(gender || '').trim().toLowerCase();
      const normGender  =
        rawGender === 'male'   || rawGender === 'm' ? 'Male'   :
        rawGender === 'female' || rawGender === 'f' ? 'Female' :
        String(gender || '').trim();

      if (!normCat || !normParam) return;

      if (!map[normCat])                          map[normCat] = {};
      if (!map[normCat][rowLabel])                map[normCat][rowLabel] = {};
      if (!map[normCat][rowLabel][normGender])    map[normCat][rowLabel][normGender] = {};

      map[normCat][rowLabel][normGender][normAge] =
        (map[normCat][rowLabel][normGender][normAge] || 0) + Number(count);
    });

    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, paramMap]) => ({
        category,
        parameters: Object.keys(paramMap).sort(),
        dataMap:    paramMap,
      }));
  };

  const totalMatches = useMemo(() => {
    if (!globalFilter.trim()) return null;
    return sections.reduce((sum, sec) =>
      sum + sec.parameters.filter(d =>
        d.toLowerCase().includes(globalFilter.toLowerCase())
      ).length, 0
    );
  }, [sections, globalFilter]);

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          Please select a period to view lab activity
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No lab results found for the selected period.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Top bar: summary chips + global search ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        mb: 1.5, flexWrap: 'wrap'
      }}>
        <Chip
          label={`${sections.length} lab categories`}
          size="small"
          sx={{ bgcolor: '#455a64', color: '#fff', fontSize: '0.7rem' }}
        />
        <Chip
          label={`${sections.reduce((s, sec) => s + sec.parameters.length, 0)} result rows`}
          size="small"
          sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '0.7rem' }}
        />

        {/* Global filter — searches across every parameter in every
            category. Useful when you don't remember which lab a
            parameter belongs to. */}
        <Box sx={{ ml: 'auto', minWidth: 280 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search all lab results across all categories (e.g. 'positive', 'malaria', 'hiv')…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#90a4ae' }} />
                </InputAdornment>
              ),
              endAdornment: globalFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setGlobalFilter('')}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              bgcolor: '#fff',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { fontSize: '0.8rem' },
            }}
          />
        </Box>

        {totalMatches !== null && (
          <Chip
            label={`${totalMatches} match${totalMatches !== 1 ? 'es' : ''}`}
            size="small"
            sx={{
              bgcolor: totalMatches > 0 ? '#e65100' : '#bdbdbd',
              color: '#fff', fontSize: '0.7rem'
            }}
          />
        )}
      </Box>

      {/* ── Sections ── */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {sections.map(sec => (
          <LabSection
            key={sec.category}
            category={sec.category}
            parameters={sec.parameters}
            dataMap={sec.dataMap}
            globalFilter={globalFilter}
          />
        ))}
      </Paper>
    </Box>
  );
}
